import { useFormik } from "formik";
import { useState } from "react";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import { SingleCooperativeDetails } from "../../../../../redux/features/cooperative/cooperative.type";
import {
  LoanProduct,
  LoanRepaymentMethod,
  RequestLoanPayload,
} from "../../../../../redux/features/loans/loans.type";
import {
  SingleDebitCard,
  SingleMemberBankAccount,
} from "../../../../../redux/features/payment/payment.type";
import { loanrepaymentMethodMap } from "../../../../../utils/constants";
import { loanRequestRepaymentDetails } from "../../../../../validationSchema";
import { Button } from "../../../../buttons/button";
import { SingleBankComponent } from "../../../../cards/bankCard";
import { DebitCard } from "../../../../cards/debitcard";
import ComboBox from "../../../../inputs/combobox";
import {
  ChooseDebitInstrumentModal,
  InstrumentType,
} from "../../debitInstrument/chooseDebitInstrument";

export const RepaymentDetails = ({
  cooperativeDetails,
  onSuccess,
  finalPayload,
  setFinalPayload,
  selectedLoanProduct,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: (data: Partial<RequestLoanPayload>) => void;
  finalPayload?: Partial<RequestLoanPayload>;
  setFinalPayload: (data: Partial<RequestLoanPayload>) => void;
  selectedLoanProduct?: LoanProduct;
}) => {
  const [selectedInnstrument, setSelectedInstrument] = useState<
    SingleMemberBankAccount | SingleDebitCard
  >();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    handleBlur,
    isValid,
    validateForm,
  } = useFormik({
    initialValues: {
      repaymentMode: undefined,
      repaymentMethodId: undefined,
    },
    enableReinitialize: true,
    validationSchema: loanRequestRepaymentDetails,
    onSubmit: (data) => {
      const newPayload = {
        ...finalPayload,
        ...data,
        repaymentMethodId:
          data.repaymentMode === "card" ? data.repaymentMethodId : undefined,
      };

      setFinalPayload(newPayload);

      onSuccess(newPayload);
    },
  });

  return (
    <>
      <form className="flex flex-col gap-4 min-h-full" onSubmit={handleSubmit}>
        <ComboBox
          type="text"
          isDropdown
          name="repaymentMode"
          value={
            values.repaymentMode
              ? loanrepaymentMethodMap[
                  values?.repaymentMode as LoanRepaymentMethod
                ].text
              : ""
          }
          menuItems={Object.keys(loanrepaymentMethodMap)
            .map((k) => ({
              name: loanrepaymentMethodMap[k as LoanRepaymentMethod].text,
              value: k as LoanRepaymentMethod,
            }))
            .filter(
              (v) =>
                v.value !== "wallet" &&
                selectedLoanProduct?.payment_modes?.includes(v.value)
            )}
          placeholder="Select repayment method"
          onChange={() => {}}
          readOnly
          onMenuClose={() => {}}
          onBlur={handleBlur}
          hasIcon={false}
          errors={
            errors?.repaymentMode && touched.repaymentMode
              ? errors?.repaymentMode
              : ""
          }
          menuClassName="!max-h-[250px] !min-w-[200px] md:!z-[999]"
          position="bottom start"
          onSelectOption={(val: number) => {
            setFieldValue("repaymentMode", val);
            setFieldValue("repaymentMethodId", undefined);

            setTimeout(() => {
              validateForm();
            }, 500);
          }}
          className=""
          hasTitle
          title="How do you plan to repay your loan?"
          containerClassName="!w-full"
          immediate
          loading={false}
        />

        {values.repaymentMode === "card" || values.repaymentMode === "bank" ? (
          <div className="flex flex-col gap-3">
            <p className="font-bold text-sm">
              Link {values.repaymentMode === "card" ? "card" : "account"}
            </p>

            {values.repaymentMode === "card" && values.repaymentMethodId ? (
              <DebitCard
                cardDetails={
                  (selectedInnstrument as SingleDebitCard).CardDetail
                }
                enableDelete={false}
                onSuccess={() => {}}
                id={(selectedInnstrument as SingleDebitCard).id}
              />
            ) : undefined}

            {values.repaymentMode === "bank" && values.repaymentMethodId ? (
              <SingleBankComponent
                accountName={
                  (selectedInnstrument as SingleMemberBankAccount)?.AccountName
                }
                accountNumber={
                  (selectedInnstrument as SingleMemberBankAccount)
                    ?.AccountNumber
                }
                bankName={
                  (selectedInnstrument as SingleMemberBankAccount)?.BankName
                }
                canDelete={false}
                accountId={(selectedInnstrument as SingleMemberBankAccount).id}
              />
            ) : undefined}

            <Button
              text={`${
                values.repaymentMethodId ? "Link a different" : "Link"
              } ${values.repaymentMode === "bank" ? "account" : "card"}`}
              icon="plus"
              onClick={async () => {
                handleModalOpen("chooseDebitInstrumentModal");
              }}
              type="button"
              className="w-full"
              variation="plain"
            />
          </div>
        ) : undefined}

        <div className="pt-4 mt-auto border-t">
          <Button
            type="submit"
            className=""
            text="Proceed"
            disabled={!isValid}
          />
        </div>
      </form>

      {values.repaymentMode ? (
        <ChooseDebitInstrumentModal
          isModalOpen={modalOpen && modal === "chooseDebitInstrumentModal"}
          cooperativeDetails={cooperativeDetails}
          type={[values.repaymentMode] as InstrumentType[]}
          handleModalClose={() => {
            handleModalClose();
          }}
          onSuccess={(selected) => {
            setSelectedInstrument(selected);
            setFieldValue("repaymentMethodId", selected.id);
            handleModalClose();
          }}
          mode="selection"
        />
      ) : undefined}
    </>
  );
};
